import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { useTour } from "../lib/tours/useTour";
import { useDashboardSteps } from "../lib/tours/steps/dashboard";
import TourReplayButton from "../lib/tours/TourReplayButton";

import HeroCommandCenter from "../components/qenva/HeroCommandCenter";
import PriorityRecommendationCard from "../components/qenva/PriorityRecommendationCard";
import SuggestionCard from "../components/SuggestionCard";
import { SUGGESTION_DEFINITIONS } from "../lib/suggestions/suggestionsManifest";
import { useSuggestions } from "../lib/suggestions/useSuggestions";
import EvolutionGuardianStrip from "../components/qenva/EvolutionGuardianStrip";
import BusinessKPIGrid from "../components/qenva/BusinessKPIGrid";
import PathTo100Card from "../components/qenva/PathTo100Card";
import HealthRadarExpanded from "../components/qenva/HealthRadarExpanded";
import PrioritiesTodayGrid from "../components/qenva/PrioritiesTodayGrid";
import CashflowForecastCard from "../components/qenva/CashflowForecastCard";
import OpportunitiesGuardianGrid from "../components/qenva/OpportunitiesGuardianGrid";
import TotalPreservedValueBlock from "../components/qenva/TotalPreservedValueBlock";
import SimulationPredictions from "../components/qenva/SimulationPredictions";
import QuickActions from "../components/qenva/QuickActions";
import ExecutiveReportLauncher from "../components/qenva/ExecutiveReportLauncher";

/**
 * QENVA Dashboard V3 — Guardian™ Command Center.
 *
 * Single-call data fetch on /api/dashboard/overview-v3.
 * Composes 14 Figma-validated sections in this order:
 *   1. Hero Guardian (status + greeting + protection message + score + KPIs)
 *   2. Recommandation Guardian™ — Priorité absolue aujourd'hui
 *   3. Évolution Guardian™ (4-month trail)
 *   4. KPIs Business (Exposition · Préservée · Opportunités · Risques)
 *   5. Chemin vers 100™ + Radar Santé Entreprise™ (2-col grid)
 *   6. Priorités du Jour (2×2 ranked cards)
 *   7. Opportunités Guardian™ (4 cards with confidence / difficulty)
 *   8. Valeur Préservée Totale™ (full-width navy hero block)
 *   9. Prédictions Guardian™ — Simulation
 *  10. Quick Actions
 *
 * Empty states for every section; sticky-aware layout.
 */
export default function Dashboard() {
  const { t, i18n } = useTranslation("dashboard");
  const { t: tSuggestions } = useTranslation("suggestions");
  const { formatRelative } = useLocaleFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const dashboardSteps = useDashboardSteps();
  const { Tour, replay } = useTour("dashboard", dashboardSteps, { ready: !!data });

  const fetchOverview = useCallback(async () => {
    setRefreshing(true);
    try {
      const locale = (i18n.language || "fr").startsWith("fr") ? "fr" : "en";
      const [overview, evo12m] = await Promise.all([
        api.get("/dashboard/overview-v3", { params: { locale } }),
        api.get("/dashboard/evolution-12m", { params: { locale, months: 12 } })
          .catch(() => ({ data: null })),  // graceful fallback
      ]);
      const merged = { ...overview.data };
      if (evo12m?.data) {
        merged.evolution_12m = evo12m.data;
      }
      setData(merged);
      setError(null);
      return true;
    } catch (e) {
      // Keep any previously-loaded `data` as-is (stale-but-labeled beats wiped),
      // but never let a failed fetch fall through to every section's "all clear"
      // empty state silently — see the `error` branches below.
      const message = formatApiErrorDetail(e.response?.data?.detail) || t("load_error");
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const generatedRelative = data?.generated_at ? formatRelative(data.generated_at) : null;

  const dashboardSuggestionDefs = SUGGESTION_DEFINITIONS.filter((d) => d.page === "dashboard");
  const { visible: visibleSuggestions, dismiss: dismissSuggestion } = useSuggestions(dashboardSuggestionDefs, data);

  const handleAnalyze = useCallback(async () => {
    toast.message(t("analyzing_toast"));
    const ok = await fetchOverview();
    if (ok) toast.success(t("analyzed_toast"));
  }, [fetchOverview, t]);

  if (loading && !data) {
    return (
      <div className="grid place-items-center min-h-[60vh] text-zinc-400" data-testid="dashboard-loading">
        <p className="text-[13px]">{t("loading_hero")}</p>
      </div>
    );
  }

  // No data at all, and the load failed: never let the 14 sections below render
  // their individual "nothing to worry about" empty states here — that would be
  // indistinguishable from a genuine, confirmed zero-risk check. Show one honest,
  // recoverable error state instead.
  if (error && !data) {
    return (
      <div className="grid place-items-center min-h-[60vh] px-6" data-testid="dashboard-error">
        <div className="max-w-md text-center space-y-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 grid place-items-center mx-auto">
            <AlertTriangle size={22} strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-display font-bold text-qenva-navy text-[17px] tracking-tight">
              {t("error_title")}
            </h2>
            <p className="text-zinc-500 text-[13px] mt-2 leading-relaxed">
              {t("error_body")}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchOverview}
            disabled={refreshing}
            data-testid="dashboard-error-retry"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-bold bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors mx-auto"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7 animate-fade-up pb-8" data-testid="dashboard-v3-page">
      {Tour}
      <div className="flex justify-end -mb-3">
        <TourReplayButton onClick={replay} />
      </div>
      {/* Stale-data notice: a refresh failed but we're still showing the last
          successful load, so sections below keep rendering real (if aging)
          data instead of being wiped to their empty states. */}
      {error && data && (
        <div
          className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-[13px]"
          role="status"
          data-testid="dashboard-stale-banner"
        >
          <AlertTriangle size={15} className="shrink-0" />
          <span className="flex-1">
            {t("stale_banner", { time: generatedRelative || t("stale_banner_fallback_time") })}
          </span>
          <button
            type="button"
            onClick={fetchOverview}
            disabled={refreshing}
            data-testid="dashboard-stale-retry"
            className="font-bold underline underline-offset-2 shrink-0 disabled:opacity-50"
          >
            {t("retry")}
          </button>
        </div>
      )}

      {/* 1. Hero Guardian Command Center */}
      <HeroCommandCenter
        data={data}
        lastAnalysis={generatedRelative}
        onAnalyze={handleAnalyze}
        onOpenReport={() => setReportOpen(true)}
        testId="dash-hero"
      />

      {visibleSuggestions.map((def) => (
        <SuggestionCard
          key={def.key}
          suggestionKey={def.key}
          icon={def.icon}
          title={tSuggestions(`${def.key}.title`, def.countFn ? { count: def.countFn(data) } : undefined)}
          subtitle={tSuggestions(`${def.key}.subtitle`)}
          ctaLabel={tSuggestions(`${def.key}.cta`)}
          ctaHref={def.ctaHref}
          onDismiss={() => dismissSuggestion(def.key)}
        />
      ))}

      {/* 2. Recommandation Guardian™ — Priorité absolue */}
      <PriorityRecommendationCard
        recommendation={data?.priority_recommendation}
        testId="dash-priority-rec"
      />

      {/* 3. Évolution Guardian™ — prefers real 12-month series, falls back to 4-month overview */}
      <EvolutionGuardianStrip
        evolution={data?.evolution_12m || data?.evolution}
        target={(data?.evolution_12m || data?.evolution)?.target || 100}
        testId="dash-evolution"
      />

      {/* 4. KPIs Business */}
      <BusinessKPIGrid
        businessKpis={data?.business_kpis}
        testId="dash-business-kpis"
      />

      {/* 5. Chemin vers 100™ + Radar Santé Entreprise™ (2-col grid on desktop) */}
      <div className="grid lg:grid-cols-2 gap-5">
        <PathTo100Card pathTo100={data?.path_to_100} testId="dash-path-to-100" />
        <HealthRadarExpanded healthRadar={data?.health_radar} testId="dash-health-radar" />
      </div>

      {/* 5-bis. Trésorerie à venir™ — only renders when the workspace has unpaid invoices */}
      <CashflowForecastCard testId="dash-cashflow" />

      {/* 6. Priorités du Jour */}
      <PrioritiesTodayGrid
        priorities={data?.priorities_today}
        testId="dash-priorities-today"
      />

      {/* 7. Opportunités Guardian™ */}
      <OpportunitiesGuardianGrid
        opportunities={data?.opportunities}
        testId="dash-opportunities"
      />

      {/* 8. Valeur Préservée Totale™ */}
      <TotalPreservedValueBlock
        value={data?.total_preserved_value}
        onOpenReport={() => setReportOpen(true)}
        testId="dash-total-preserved"
      />

      {/* 9. Prédictions Guardian™ */}
      <SimulationPredictions
        simulation={data?.simulation}
        testId="dash-simulation"
      />

      {/* 10. Quick Actions */}
      <QuickActions
        onCustomAction={(k) => k === "exec-report" && setReportOpen(true)}
        testId="dash-quick-actions"
      />

      <ExecutiveReportLauncher open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}
